import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from "react-hook-form";
import { MapPin, Map as MapIcon, Crosshair } from 'react-feather';
import Button from 'react-bootstrap/Button';
import { useWindowManager } from '../Window';
import WindowBody from '../Window/WindowBody';
import WindowFooter from '../Window/WindowFooter';
import { useNotification } from '../Core/NotificationProvider';
import { get } from '../../services/ApiService';
import { labelSizeClass } from './labelSize';
import './UiInputLocalizacao.scss';

// Leaflet imports
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para os ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapPickerProps {
    initialPosition: [number, number];
    onSave: (position: [number, number]) => void;
    triggerClose: () => void;
}

function MapPicker({ initialPosition, onSave, triggerClose }: MapPickerProps) {
    const [mapLayer, setMapLayer] = useState<'standard' | 'satellite'>('standard');
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);
    const tileLayerRef = useRef<L.TileLayer | null>(null);
    const currentPositionRef = useRef<[number, number]>(initialPosition);

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current).setView(initialPosition, 13);
        tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);

        const marker = L.marker(initialPosition, { draggable: true }).addTo(map);
        marker.on('dragend', () => {
            const pos = marker.getLatLng();
            currentPositionRef.current = [pos.lat, pos.lng];
        });
        map.on('click', (e: L.LeafletMouseEvent) => {
            currentPositionRef.current = [e.latlng.lat, e.latlng.lng];
            marker.setLatLng(e.latlng);
        });

        mapRef.current = map;
        markerRef.current = marker;

        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
            tileLayerRef.current = null;
        };
    }, []);

    const toggleMapLayer = () => {
        const newLayer = mapLayer === 'standard' ? 'satellite' : 'standard';
        setMapLayer(newLayer);
        if (mapRef.current && tileLayerRef.current) {
            tileLayerRef.current.remove();
            tileLayerRef.current = newLayer === 'satellite'
                ? L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                    attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                  }).addTo(mapRef.current)
                : L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  }).addTo(mapRef.current);
        }
    };

    return (
        <>
            <WindowBody noPadding>
                <div style={{ position: 'relative', height: '500px', width: '100%' }}>
                    <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
                    <Button
                        variant="light"
                        size="sm"
                        onClick={toggleMapLayer}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <MapIcon size={14} />
                        {mapLayer === 'standard' ? 'Satélite' : 'Padrão'}
                    </Button>
                </div>
            </WindowBody>
            <WindowFooter>
                <Button variant="secondary" onClick={triggerClose}>Fechar</Button>
                <Button variant="primary" onClick={() => onSave(currentPositionRef.current)}>Definir localização</Button>
            </WindowFooter>
        </>
    );
}

interface UiInputLocalizacaoProps {
    name: string;
    label: string;
    size?: number;
    labelSize?: number;
    /** Os campos de endereço do form, para o botão que busca a coordenada pelo endereço. */
    camposEndereco?: {
        logradouro?: string;
        numero?: string;
        bairro?: string;
        cidade?: string;
    };
}

const DEFAULT_POSITION: [number, number] = [-27.5969, -48.5495]; // Florianópolis, SC

export default function UiInputLocalizacao({ name, label, size, labelSize, camposEndereco }: UiInputLocalizacaoProps) {
    const { register, setValue, watch, getValues } = useFormContext();
    const windowManager = useWindowManager();
    const notification = useNotification();
    const value = watch(name);
    const [detectando, setDetectando] = useState(false);

    const detectar = async () => {
        if (!camposEndereco) return;

        const cidade = getValues(camposEndereco.cidade ?? '');

        const parametros = new URLSearchParams({
            logradouro: getValues(camposEndereco.logradouro ?? '') ?? '',
            numero: getValues(camposEndereco.numero ?? '') ?? '',
            bairro: getValues(camposEndereco.bairro ?? '') ?? '',
            cidade: (typeof cidade === 'object' ? cidade?.nome : cidade) ?? '',
            estado: (typeof cidade === 'object' ? cidade?.estado : '') ?? '',
        });

        setDetectando(true);
        try {
            const resposta = await get(`/?route=util&action=geocodificar&${parametros.toString()}`);
            if (resposta.data?.success && resposta.data.data?.localizacao) {
                setValue(name, resposta.data.data.localizacao, { shouldDirty: true });
            } else {
                notification.error(resposta.data?.message || 'Não foi possível localizar o endereço');
            }
        } catch (e) {
            notification.error('Erro de comunicação ao localizar o endereço');
        }
        setDetectando(false);
    };

    const parseCoordinates = (input: string): [number, number] | null => {
        if (!input) return null;
        const gmapsMatch = input.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (gmapsMatch) return [parseFloat(gmapsMatch[1]), parseFloat(gmapsMatch[2])];
        const parts = input.split(',').map(p => p.trim());
        if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lng)) return [lat, lng];
        }
        return null;
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const coords = parseCoordinates(e.target.value);
        if (coords) setValue(name, `${coords[0]}, ${coords[1]}`);
    };

    const handleOpenMap = () => {
        const initialPosition = parseCoordinates(value) ?? DEFAULT_POSITION;
        let windowId: string;

        const handleSave = (position: [number, number]) => {
            setValue(name, `${position[0].toFixed(6)}, ${position[1].toFixed(6)}`);
            windowManager.closeWindow(windowId);
        };

        const handleClose = () => windowManager.closeWindow(windowId);

        windowId = windowManager.openWindow({
            title: 'Selecionar Localização',
            component: MapPicker,
            size: { width: 700 },
            props: {
                initialPosition,
                onSave: handleSave,
                triggerClose: handleClose,
            }
        });
    };

    return (
        <div className={`col-lg-${size || 12} ui-input-localizacao`}>
            <div className={`form-item${labelSizeClass(labelSize)}`}>
                <label className="form-label">
                    {label}
                    <span className="form-label-acoes">
                        { camposEndereco &&
                            <Button variant="link" onClick={detectar} disabled={detectando} title="Buscar coordenada pelo endereço">
                                <Crosshair size={14} />
                            </Button>
                        }
                        <Button variant="link" onClick={handleOpenMap} title="Escolher no mapa">
                            <MapPin size={14} />
                        </Button>
                    </span>
                </label>
                <input
                    type="text"
                    className="form-control"
                    {...register(name)}
                    onBlur={handleBlur}
                    placeholder="Cole link do Google Maps ou Lat, Long"
                />
            </div>
        </div>
    );
}
